import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Filter state stored in the URL instead of React state.
 *
 * Two things this buys that component state cannot: a view like "trending Tamil
 * music in India" becomes a link someone can send, and a refresh no longer throws
 * the visitor back to defaults. Values equal to the default are dropped from the
 * query string so ordinary URLs stay clean.
 */
export const useFilterState = (defaults) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const current = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const raw = searchParams.get(key);
      if (raw === null) continue;
      current[key] = typeof defaults[key] === "number" ? Number(raw) || 0 : raw;
    }
    return current;
  }, [searchParams, defaults]);

  const setFilter = useCallback(
    (patch) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === defaults[key] || value === "" || value === null || value === undefined) {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      }
      // replace: filter tweaks should not each become a back-button step
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, defaults]
  );

  const resetFilters = useCallback(() => setSearchParams({}, { replace: true }), [setSearchParams]);

  const activeCount = useMemo(
    () => Object.keys(defaults).filter((k) => filters[k] !== defaults[k]).length,
    [filters, defaults]
  );

  return { filters, setFilter, resetFilters, activeCount };
};
